import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/ui/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/ui/ui/card';
import { useAuth } from '@/contexts/useAuth';
import { SportIcon } from '@/ui/shared/SportIcon';
import { RunningIcon } from '@/ui/icons/common/RunningIcon';
import {
  REGISTER_BUSINESS_FRESH_ENTRY_STATE,
  REGISTER_BUSINESS_PATH,
} from '@/modules/workflow-entry/core/registerBusinessRoute';
import { FAVORITE_SPORTS } from './indexFavoriteSports';
import {
  Trophy,
  Calendar,
  Clock,
  MapPin,
  Shield,
  Smartphone,
  CreditCard,
  Users,
  ArrowRight,
  CheckCircle,
} from 'lucide-react';

const Index = () => {
  const { user, isLoading, signOut } = useAuth();
  const showAuthenticatedCtas = !isLoading && Boolean(user);
  const handleSignOut = async () => {
    await signOut();
  };

  const features = [
    {
      icon: Calendar,
      title: 'Easy Booking',
      description: 'Book courts in seconds with our intuitive interface. View real-time availability across all businesses.',
    },
    {
      icon: Clock,
      title: 'Flexible Scheduling',
      description: 'Set up recurring bookings, join waitlists, and reschedule with just a few taps.',
    },
    {
      icon: MapPin,
      title: 'Multiple Venues',
      description: 'Find and book courts at various sports businesses. One app for all your sporting needs.',
    },
    {
      icon: CreditCard,
      title: 'Secure Payments',
      description: 'Pay per booking or enjoy free courts. Secure payment processing with instant confirmations.',
    },
    {
      icon: Smartphone,
      title: 'Mobile Ready',
      description: 'Book on the go with our mobile-optimized app. Available on iOS and Android.',
    },
    {
      icon: Shield,
      title: 'Trusted Platform',
      description: 'Join thousands of sports enthusiasts who trust SportBook for their court reservations.',
    },
  ];

  // Spread racket sports through the strip so repeated icons do not sit side by side.
  const marqueeSports = [...FAVORITE_SPORTS, ...FAVORITE_SPORTS];

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-border/80 bg-background/95 shadow-sm backdrop-blur supports-[backdrop-filter]:bg-background/85">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Trophy className="h-7 w-7 text-primary" />
            <span className="text-xl font-bold">SportBook</span>
          </Link>
          <div className="flex items-center gap-4">
            <Button variant="ghost" asChild>
              <Link to="/pricing">Pricing</Link>
            </Button>
            {showAuthenticatedCtas ? (
              <>
                <Button variant="outline" onClick={handleSignOut}>
                  Logout
                </Button>
                <Button asChild>
                  <Link to="/dashboard">Go to Dashboard</Link>
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" asChild>
                  <Link to="/signin">Login</Link>
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 md:py-32">
        <div className="container mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
            <Trophy className="h-4 w-4" />
            The #1 Sports Court Booking Platform
          </div>
          <h1 className="text-4xl md:text-6xl font-bold tracking-tight mb-6 max-w-4xl mx-auto">
            Book Your Game,{' '}
            <span className="text-primary">Anytime, Anywhere</span>
          </h1>
          <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto leading-relaxed">
            <span className="md:block">Find and book sports courts at top businesses near you.</span>{' '}
            <span className="md:block">Tennis, badminton, squash, and more.</span>{' '}
            <span className="md:block">Real-time availability, instant confirmations, seamless payments.</span>
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Button size="lg" asChild className="hero-cta-pulse-primary">
              <Link to="/signup">
                Start Playing
                <RunningIcon className="ml-2.5 h-6 w-6 text-current" size={24} />
              </Link>
            </Button>
            <Button size="lg" asChild className="hero-cta-pulse-accent bg-accent text-accent-foreground hover:bg-accent/90">
            <Link to={REGISTER_BUSINESS_PATH} state={REGISTER_BUSINESS_FRESH_ENTRY_STATE}>Register Your Business</Link>
          </Button>
          </div>
          <p className="mt-4 text-sm text-muted-foreground">
            Create and set up a business account in minutes.
          </p>
        </div>
      </section>

      {/* Sports Section */}
      <section className="py-16 bg-muted/50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">All Your Favorite Sports</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              From racket sports to team games, book courts for any sport you love.
            </p>
          </div>
          <div className="overflow-x-auto pb-2 md:overflow-hidden">
            <div className="sports-marquee flex w-max gap-4">
              {marqueeSports.map((sport, index) => (
                <Card key={`${sport}-${index}`} className="group w-40 shrink-0 text-center transition-all hover:border-primary/20 hover:bg-primary/5 hover:shadow-md md:w-44">
                  <CardContent className="pt-6">
                    <div className="mb-3 flex justify-center text-primary transition-colors duration-150 group-hover:text-primary">
                      <SportIcon sportName={sport} size={30} />
                    </div>
                    <h3 className="font-semibold text-foreground">{sport}</h3>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">Why Choose SportBook?</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              SportBook provides everything you need to manage your sports bookings in one powerful platform.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature) => (
              <Card key={feature.title} className="border-0 shadow-sm hover:shadow-md transition-shadow">
                <CardHeader>
                  <div className="p-3 rounded-lg bg-primary/10 w-fit mb-4">
                    <feature.icon className="h-6 w-6 text-primary" />
                  </div>
                  <CardTitle>{feature.title}</CardTitle>
                  <CardDescription className="text-base">{feature.description}</CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* For Businesses Section */}
      <section className="py-20 bg-primary text-primary-foreground">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6">For Business Owners and Staff</h2>
              <p className="text-lg opacity-90 mb-8">
                Create your business account, add your courts, and start taking bookings in minutes.
              </p>
              <ul className="space-y-4">
                {[
                  'Configurable sports with custom durations',
                  'Flexible court management',
                  'Real-time booking calendar',
                  'Payment processing & reporting',
                  'Staff management & roles',
                  'Waitlist management',
                ].map((item) => (
                  <li key={item} className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex justify-center">
              <Card className="w-full max-w-md bg-white/10 border-white/20">
                <CardHeader>
                  <CardTitle className="text-primary-foreground">Register Your Business</CardTitle>
                  <CardDescription className="text-primary-foreground/80">
                    Get your sports business live on SportBook in minutes. No setup fees.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Button className="w-full bg-white text-primary hover:bg-white/90" asChild>
                    <Link to={REGISTER_BUSINESS_PATH} state={REGISTER_BUSINESS_FRESH_ENTRY_STATE}>
                      <Users className="mr-2 h-5 w-5" />
                      Create Business Account
                    </Link>
                  </Button>
                  <Button className="w-full bg-white/10 border-white/30 text-primary-foreground hover:bg-white/20" variant="outline" asChild>
                    <Link to="/pricing">View Pricing Plans</Link>
                  </Button>
                  <p className="text-sm text-center text-primary-foreground/70">
                    Already have a business account?{' '}
                    <Link to="/signin" className="underline text-primary-foreground hover:text-primary-foreground/80">
                      Log in
                    </Link>
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Book Your Next Game?</h2>
          <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
            Join thousands of players who book their courts with SportBook every day.
          </p>
          <Button size="lg" asChild>
            <Link to="/signup">
              Create Free Account
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t py-12">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2">
              <Trophy className="h-6 w-6 text-primary" />
              <span className="font-bold">SportBook</span>
            </div>
            <div className="flex items-center gap-6">
              <Link to="/pricing" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                Pricing
              </Link>
              <p className="text-sm text-muted-foreground">
                © {new Date().getFullYear()} SportBook. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Index;

